import { isDiscordActivity, getWsUrl, getDiscordSdk } from '../discord.ts';
import { startMediaEngine, type MediaEngineHandle } from '../workers/media-engine-proxy.ts';
import type { StreamInfo } from '../ws/types.ts';
import { getLiveCredentials } from '../live-id.ts';
import { createContextMenu } from './context-menu.ts';
import { createGrid } from './grid.ts';
import { setDiagLogs, diagLogsEnabled } from '../diag-logs.ts';
import { handleRefreshMessage } from '../ws/version.ts';

const wsServerUrl = getWsUrl();
const broadcastUrl = 'https://activity.ikx-server.com/broadcast.html';

let viewerHandle: MediaEngineHandle | null = null;

const engineProxy: MediaEngineHandle = {
  stop: () => viewerHandle?.stop(),
  send: (data) => viewerHandle?.send(data),
  setWatching: (streamId, watching) => viewerHandle?.setWatching(streamId, watching),
  setLatencyMode: (streamId, mode) => viewerHandle?.setLatencyMode(streamId, mode),
  requestSnapshot: (streamId) => viewerHandle?.requestSnapshot(streamId),
  registerVideoStream: (streamId) => viewerHandle?.registerVideoStream(streamId),
  unregisterVideoStream: (streamId) => viewerHandle?.unregisterVideoStream(streamId),
  setVideoPaused: (streamId, paused) => viewerHandle?.setVideoPaused(streamId, paused),
};

export async function mountApp(root: HTMLElement) {
  const inDiscord = isDiscordActivity();
  root.innerHTML = `
    <div id="stream-app" class="${inDiscord ? 'discord' : 'standalone'}">
      <div id="stage"></div>
      <button id="fab-broadcast" class="fab" title="Compartilhar tela">
        <svg viewBox="0 0 24 24" fill="none"><rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" stroke-width="2"/><path d="M8 21h8M12 18v3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        <span>Transmitir</span>
      </button>
    </div>
  `;

  const stage = root.querySelector('#stage') as HTMLElement;
  const fabBroadcast = root.querySelector('#fab-broadcast') as HTMLButtonElement;

  const contextMenu = createContextMenu();

  const grid = createGrid(
    stage,
    engineProxy,
    (tile, x, y) => {
    if (grid.isSelectionLocked()) return;
    const isStatsActive = tile.element.querySelector('.tile-stats')?.classList.contains('active') || false;
    const isMuted = tile.element.classList.contains('muted');
    const isShowId = tile.element.classList.contains('show-id');
    const isWatching = tile.watching;
    const isSmooth = grid.getLatencyMode(tile.streamId) === 'smooth';
    const menuItems: import('./context-menu.ts').ContextMenuItem[] = [
      {
        label: 'Assistir',
        type: 'check',
        checked: isWatching,
        onClick: () => {
          if (tile.watching) {
            tile.onWatchStop?.();
          } else {
            tile.onWatch?.();
          }
        },
      },
      {
        label: 'Mostrar ID',
        type: 'check',
        checked: isShowId,
        onClick: () => tile.onShowIdToggle?.(),
      },
      {
        label: 'Mostrar stats',
        type: 'check',
        checked: isStatsActive,
        onClick: () => {
          const stats = tile.element.querySelector('.tile-stats');
          stats?.classList.toggle('active');
          const statsBtn = tile.element.querySelector('.stats-btn');
          statsBtn?.classList.toggle('active', stats?.classList.contains('active') || false);
        },
      },
      {
        label: 'Logar stats',
        type: 'check',
        checked: diagLogsEnabled(),
        onClick: () => {
          setDiagLogs(!diagLogsEnabled());
        },
      },
      {
        label: 'Aumentar buffer (+3s)',
        type: 'check',
        checked: isSmooth,
        onClick: () => {
          const newMode = isSmooth ? 'low' : 'smooth';
          grid.setLatencyMode(tile.streamId, newMode);
        },
      },
      { type: 'separator' },
      {
        label: 'Volume',
        type: 'slider',
        value: tile.volume,
        onChange: (value) => tile.onVolumeChange?.(value),
      },
      { label: 'Silenciar', type: 'check', checked: isMuted, onClick: () => tile.onMuteToggle?.() },
    ];
    contextMenu.show(x, y, menuItems);
    }
  );

  // No Discord, quando a janela fica bem pequena (PiP da call), o grid vira
  // uma selecao travada: a primeira stream expandida sozinha, sem opcao de
  // troca. Ao voltar ao tamanho normal, o grid reaparece como estava.
  const PIP_MAX_WIDTH = 420;
  const PIP_MAX_HEIGHT = 240;

  function syncPipSelection() {
    const small = window.innerWidth <= PIP_MAX_WIDTH || window.innerHeight <= PIP_MAX_HEIGHT;
    if (!inDiscord || !small) {
      grid.setLockedSelection(null);
      return;
    }
    if (grid.hasExpanded()) return;
    grid.setLockedSelection(grid.getPrimaryStreamId());
  }

  window.addEventListener('resize', syncPipSelection);

  // Guarda de geracao: se varios onDisconnect/teardown dispararem (zumbi
  // abandonada + onclose tardio, por exemplo), so a geracao mais recente
  // reconecta. Sem isso, viewers se acumulam no servidor.
  let viewerGeneration = 0;

  async function startViewing() {
    const gen = ++viewerGeneration;
    const reconnect = () => {
      setTimeout(() => {
        if (gen === viewerGeneration) startViewing();
      }, 2000);
    };
    try {
      const { liveId, liveToken } = getLiveCredentials();
      viewerHandle = startMediaEngine(
        wsServerUrl,
        {
          onAudioFrame: (streamId, audioBuffer, presentationTime, channels, sampleCount, sampleRate, timestamp) => {
            grid.handleAudioFrame(streamId, audioBuffer, presentationTime, channels, sampleCount, sampleRate, timestamp);
          },
          onVideoFrame: (streamId, frame, presentationTime) => {
            grid.handleVideoFrame(streamId, frame, presentationTime);
          },
          onFirstFrame: (streamId) => {
            grid.handleFirstFrame(streamId);
          },
          onMetrics: (streamId, metrics) => {
            grid.handleMetrics(streamId, metrics);
          },
          onState: (streamId, _broadcasters, viewers) => {
            grid.updateStreamState(streamId, 0, viewers);
          },
          onStreams: (streams: StreamInfo[]) => {
            grid.updateStreams(streams);
            syncPipSelection();
          },
          onRefresh: (expected) => handleRefreshMessage(expected),
          onWorkerRefresh: (expected) => {
            console.log('[app] worker-refresh solicitado', expected);
            viewerHandle?.stop();
            reconnect();
          },
          onDisconnect: reconnect,
        },
        '*',
        liveId,
        liveToken
      );
      // Engine novo nasce sem subscriptions no servidor: reenvia watching
      // das tiles ativas e pede snapshot pra repintar sem esperar keyframe.
      grid.resubscribeAll();
    } catch (e) {
      console.error(e);
      reconnect();
    }
  }

  async function startBroadcasting() {
    if (isDiscordActivity()) {
      try {
        const discordSdk = await getDiscordSdk();
        if (!discordSdk) {
          console.error('[app] VITE_DISCORD_CLIENT_ID nao configurado no build; nao foi possivel abrir o broadcast externo');
          return;
        }
        await discordSdk.commands.openExternalLink({ url: broadcastUrl });
      } catch (e) {
        console.error(e);
      }
      return;
    }

    // Standalone: a transmissao tambem acontece na pagina de broadcast
    // (WHIP); o index so assiste.
    window.open(broadcastUrl, '_blank');
  }

  fabBroadcast.addEventListener('click', () => {
    startBroadcasting();
  });

  window.addEventListener('beforeunload', () => {
    viewerHandle?.stop();
  });

  startViewing();
}
